import { useState } from "react";
import { useReceiptDetail } from "hooks/useReceiptDetail";
import { useReceiptAssets } from "hooks/useReceiptAssets";
import ReceiptViewer from "components/common/ReceiptViewer";
import EditReceiptDrawer from "components/common/EditReceiptDrawer";
import { toFileList } from "utils/receipts/receiptUpload";
import { useAdminTools } from "hooks/useAdminTools";
import { useSupportedReceiptFiles } from "hooks/useSupportedReceiptFiles";

interface ReceiptReviewDrawerProps {
  open: boolean;
  onClose: () => void;
  receiptId: string;
  onOpenConnectDrawer: () => void;
  /**
   * Removing the last asset marks the whole receipt DELETED server-side
   * ("If there are no more assets attached, just update receipt status"), and
   * the library query excludes that status. Without this the row lingers and
   * the drawer sits on a record that no longer exists.
   */
  onReceiptEmptied: () => void;
}

const ReceiptReviewDrawer: React.FC<ReceiptReviewDrawerProps> = ({
  open,
  onClose,
  receiptId,
  onOpenConnectDrawer,
  onReceiptEmptied,
}) => {
  const showUploader = useAdminTools();
  const keepSupported = useSupportedReceiptFiles();
  const { receiptDetail, refetchReceiptDetail, loadingReceiptDetail } =
    useReceiptDetail(receiptId);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);

  const assetCount = receiptDetail?.assets?.length ?? 0;

  const {
    uploadReceipts,
    uploadingReceipts,
    removeReceiptAsset,
    removingReceiptAsset,
  } = useReceiptAssets({
    target: { kind: "receipt", receiptId },
    receiptId,
    existingAssets: receiptDetail?.assets ?? [],
    onChanged: refetchReceiptDetail,
  });

  const handleAddReceipts = async (picked: File[]) => {
    // `accept` only filters the click picker; a dragged file arrives whatever its type.
    const supported = keepSupported(picked);
    if (supported.length === 0) return;
    await uploadReceipts(toFileList(supported));
  };

  const handleRemoveAsset = async (assetId: string) => {
    const wasLast = assetCount === 1;
    const removed = await removeReceiptAsset(assetId);
    // Removing the only image is a supported route, not an edge case: the
    // confirmation says it deletes the receipt, and the server agrees. Gated on
    // success, since a failed removal leaves a record that still exists.
    if (removed && wasLast) onReceiptEmptied();
  };

  return (
    <>
      <ReceiptViewer
        open={open}
        receipt={receiptDetail}
        loading={loadingReceiptDetail}
        onClose={onClose}
        onEdit={() => setOpenEditDrawer(true)}
        onConnect={onOpenConnectDrawer}
        onAddAssets={handleAddReceipts}
        onRemoveAsset={handleRemoveAsset}
        busy={uploadingReceipts || removingReceiptAsset}
      />
      <EditReceiptDrawer
        open={openEditDrawer}
        onClose={() => setOpenEditDrawer(false)}
        receipt={receiptDetail}
        loading={loadingReceiptDetail}
        showUploader={showUploader}
        onUpdated={() => {
          void refetchReceiptDetail();
        }}
      />
    </>
  );
};

export default ReceiptReviewDrawer;
